import { ReceivedScoreBaseModel } from 'API/Generated/data-contracts';
import {
  buildOfflineScoreId,
  judgeOfflineDb,
  OfflineScoreRecord,
  OfflineSyncStatus,
  OfflineJudgeSessionRecord,
} from './judgeOfflineDb';

type UpsertScoresParams = {
  pageantId: number;
  eventId: number;
  eventJudgeId: number;
  rows: ReceivedScoreBaseModel[];
  syncStatus?: OfflineSyncStatus;
};

type PendingScoreGroup = {
  pageantId: number;
  eventId: number;
  eventJudgeId: number;
  rows: ReceivedScoreBaseModel[];
  recordIds: string[];
};

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getScoreRecordId = (
  eventJudgeId: number,
  row: ReceivedScoreBaseModel,
): string => {
  return buildOfflineScoreId({
    eventJudgeId,
    registeredPageantContestantId: toNumber(
      row.registeredPageantContestantId,
    ),
    aocScoreCategoryId: toNumber(row.aocScoreCategoryId),
  });
};

export const localScoreRepository = {
  async upsertScores(params: UpsertScoresParams): Promise<void> {
    const now = Date.now();

    const records: OfflineScoreRecord[] = params.rows.map((row) => {
      const id = getScoreRecordId(params.eventJudgeId, row);

      return {
        id,

        pageantId: params.pageantId,
        eventId: params.eventId,
        eventJudgeId: params.eventJudgeId,

        registeredPageantContestantId: toNumber(
          row.registeredPageantContestantId,
        ),
        aocScoreCategoryId: toNumber(row.aocScoreCategoryId),

        score: toNumber(row.score),
        notes: row.notes ?? null,

        syncStatus: params.syncStatus ?? 'pending',
        syncAttempts: 0,
        lastSyncAttemptAt: null,
        lastSyncError: null,

        createdAt: now,
        updatedAt: now,

        raw: row,
      };
    });

    await judgeOfflineDb.scores.bulkPut(records);
  },

  async getScoresForJudge(params: {
    eventId: number;
    eventJudgeId: number;
  }): Promise<OfflineScoreRecord[]> {
    return judgeOfflineDb.scores
      .where('eventJudgeId')
      .equals(params.eventJudgeId)
      .filter((record) => Number(record.eventId) === Number(params.eventId))
      .toArray();
  },

  async getPendingScores(): Promise<OfflineScoreRecord[]> {
    return judgeOfflineDb.scores
      .where('syncStatus')
      .equals('pending')
      .toArray();
  },

  async getPendingScoreGroups(): Promise<PendingScoreGroup[]> {
    const pending = await this.getPendingScores();

    const grouped = new Map<string, PendingScoreGroup>();

    for (const record of pending) {
      const key = [
        record.pageantId,
        record.eventId,
        record.eventJudgeId,
      ].join('|');

      const existing = grouped.get(key);

      if (existing) {
        existing.rows.push(record.raw);
        existing.recordIds.push(record.id);
      } else {
        grouped.set(key, {
          pageantId: record.pageantId,
          eventId: record.eventId,
          eventJudgeId: record.eventJudgeId,
          rows: [record.raw],
          recordIds: [record.id],
        });
      }
    }

    return Array.from(grouped.values());
  },

  async markScoresSynced(recordIds: string[]): Promise<void> {
    const now = Date.now();

    await judgeOfflineDb.transaction('rw', judgeOfflineDb.scores, async () => {
      await Promise.all(
        recordIds.map((id) =>
          judgeOfflineDb.scores.update(id, {
            syncStatus: 'synced',
            updatedAt: now,
            lastSyncAttemptAt: now,
            lastSyncError: null,
          }),
        ),
      );
    });
  },

  async markScoresFailed(recordIds: string[], error: unknown): Promise<void> {
    const now = Date.now();
    const errorMessage =
      error instanceof Error ? error.message : String(error ?? 'Unknown error');

    await judgeOfflineDb.transaction('rw', judgeOfflineDb.scores, async () => {
      await Promise.all(
        recordIds.map(async (id) => {
          const existing = await judgeOfflineDb.scores.get(id);

          await judgeOfflineDb.scores.update(id, {
            syncStatus: 'pending',
            syncAttempts: (existing?.syncAttempts ?? 0) + 1,
            lastSyncAttemptAt: now,
            lastSyncError: errorMessage,
            updatedAt: now,
          });
        }),
      );
    });
  },


    async saveJudgePortalSnapshot(
    cacheKey: string,
    snapshot: unknown,
  ): Promise<void> {
    await judgeOfflineDb.judgePortalSnapshots.put({
      id: cacheKey,
      snapshot,
      updatedAt: Date.now(),
    });
  },

  async getJudgePortalSnapshot<T = unknown>(
    cacheKey: string,
  ): Promise<T | null> {
    const record = await judgeOfflineDb.judgePortalSnapshots.get(cacheKey);
    return (record?.snapshot as T) ?? null;
  },

  

  async deleteJudgePortalSnapshot(cacheKey: string): Promise<void> {
    await judgeOfflineDb.judgePortalSnapshots.delete(cacheKey);
  },

    async cleanupOldSnapshots(daysOld = 7): Promise<void> {
    const cutoff =
      Date.now() - daysOld * 24 * 60 * 60 * 1000;

    const oldSnapshots = await judgeOfflineDb.judgePortalSnapshots
      .filter((x) => x.updatedAt < cutoff)
      .toArray();

    await judgeOfflineDb.judgePortalSnapshots.bulkDelete(
      oldSnapshots.map((x) => x.id),
    );
  },

  async deleteJudgeSession(eventJudgeId?: number): Promise<void> {
  if (!eventJudgeId) {
    await judgeOfflineDb.judgeSessions.clear();
    return;
  }

  const sessions = await judgeOfflineDb.judgeSessions
    .where('eventJudgeId')
    .equals(Number(eventJudgeId))
    .toArray();

  await judgeOfflineDb.judgeSessions.bulkDelete(
    sessions.map((session) => session.id),
  );
},

async deleteJudgeSessionByAccessCode(accessCode: string): Promise<void> {
  const normalized = accessCode.trim().toLowerCase();

  const sessions = await judgeOfflineDb.judgeSessions
    .where('accessCode')
    .equals(normalized)
    .toArray();

  await judgeOfflineDb.judgeSessions.bulkDelete(
    sessions.map((session) => session.id),
  );
},

async saveJudgeSession(
  session: OfflineJudgeSessionRecord,
): Promise<void> {
  await judgeOfflineDb.transaction(
    'rw',
    judgeOfflineDb.judgeSessions,
    async () => {
      await judgeOfflineDb.judgeSessions.clear();
      await judgeOfflineDb.judgeSessions.put(session);
    },
  );
},
  async getJudgeSessionByAccessCode(
    accessCode: string,
  ): Promise<OfflineJudgeSessionRecord | null> {
    const normalized = accessCode.trim().toLowerCase();

    const sessions = await judgeOfflineDb.judgeSessions
      .where('accessCode')
      .equals(normalized)
      .toArray();

    return sessions[0] ?? null;
  },

  async deleteAllScoresForJudge(params: {
    eventId: number;
    eventJudgeId: number;
  }): Promise<void> {
    const records = await this.getScoresForJudge(params);
    await judgeOfflineDb.scores.bulkDelete(records.map((record) => record.id));
  },


async deleteAllJudgeOfflineDataIfSynced(params: {
  eventId: number;
  eventJudgeId: number;
}): Promise<boolean> {
  const scores = await this.getScoresForJudge(params);

  const hasUnsyncedScores = scores.some(
    (score) => score.syncStatus !== 'synced',
  );

  // Always remove login/session
  await this.deleteJudgeSession(params.eventJudgeId);

  // If any scores are still pending/failed, keep the scores
  if (hasUnsyncedScores) {
    console.warn(
      '[localScoreRepository] Judge session removed, but scores kept because some are not synced.',
      {
        eventId: params.eventId,
        eventJudgeId: params.eventJudgeId,
        scoreCount: scores.length,
        unsyncedCount: scores.filter((s) => s.syncStatus !== 'synced').length,
      },
    );

    return false;
  }

  // All scores are synced, safe to delete judge offline score data
  await judgeOfflineDb.transaction(
    'rw',
    judgeOfflineDb.scores,
    judgeOfflineDb.judgePortalSnapshots,
    async () => {
      await judgeOfflineDb.scores.bulkDelete(
        scores.map((score) => score.id),
      );

      await judgeOfflineDb.judgePortalSnapshots.delete(
        `${params.eventJudgeId}:${params.eventId}`,
      );
    },
  );

  console.log(
    '[localScoreRepository] Deleted inactive judge synced offline data.',
    {
      eventId: params.eventId,
      eventJudgeId: params.eventJudgeId,
      deletedScoreCount: scores.length,
    },
  );

  return true;
},

};